import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, Sparkles, Zap, Brush, Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';

const characters = [
  {
    no: '01',
    title: 'Heartwood Golem',
    role: 'Tank · Taunting Sentinel · The Crimson Dirge',
    tools: ['ZBrush', 'Substance 3D Painter', 'PureRef', 'Niagara'],
    portrait: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/th5q7m1d_image.png',
    lore:
      'Carved from the bones of an old-growth forest, the Heartwood Golem walks where its kin once stood — a sentinel pulled out of stillness, taught to draw fire so smaller things can survive.',
    images: [
      {
        url: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/hu89wrb9_image.png',
        label: 'Turntable',
      },
    ],
    video:
      'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/2twq09qe_golemTaunt.mp4',
    process: [
      {
        url: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/mket6jxf_golem%20low.png',
        stage: '01',
        label: 'Low-Poly',
        note: 'Anatomy primitives — silhouette and proportions only.',
      },
      {
        url: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/xu8kcuea_golem%20high.png',
        stage: '02',
        label: 'High-Poly',
        note: 'Muscle striations, veining and surface refinement in ZBrush.',
      },
    ],
    blurb:
      'A wood-golem-inspired tank for The Crimson Dirge. Sculpted in ZBrush from a PureRef board, textured in Substance 3D, with a Niagara taunt VFX authored in Unreal.',
    icon: Zap,
  },
  {
    no: '02',
    title: 'Kaelthar, the Blood-Weep',
    role: 'Demon-Lord Warrior · The Weeping Cinder Kingdom',
    tools: ['ZBrush', 'Substance 3D', 'PureRef'],
    portrait: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/uvdjj3zg_image.png',
    lore:
      'Born in a slaughter that claimed his entire clan — betrayed by the humanity who used them as fodder — Kaelthar survived not through fury, but through an eerie, silent despair that somehow refused to die. A despair that learned to fight.',
    images: [
      {
        url: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/g0q8tqmy_orc.png',
        label: 'Turntable',
      },
    ],
    blurb:
      'One of my first full character sculpts. Built a PureRef board to lock the silhouette and Orc references, then took the model into ZBrush and finished textures in Substance 3D.',
    icon: Sparkles,
  },
  {
    no: '03',
    title: 'Mirkit Goblin',
    role: 'Rank-and-File Warrior · Common Demon Unit',
    tools: ['ZBrush', 'Substance 3D', 'PureRef'],
    portrait: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/wha8qphb_image.png',
    lore:
      'Small in stature, plentiful in numbers — the Mirkit are the cheap edge of every demon front line. They die first, but they keep coming.',
    images: [
      {
        url: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/i8zxyx4g_image.png',
        label: 'Textured',
      },
    ],
    process: [
      {
        url: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/xtyj5fo0_image.png',
        stage: '01',
        label: 'Sculpt',
        note: 'Untextured high-poly pass in ZBrush — silhouette, anatomy and proportions locked from the PureRef board.',
      },
      {
        url: 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/i8zxyx4g_image.png',
        stage: '02',
        label: 'Textured',
        note: 'Substance 3D texturing pass. UV unwrapping was the hardest part of this build — visible stretching across the shirt is where I learned the most about clean seams and texel density.',
      },
    ],
    blurb:
      'A simple function-over-form warrior unit, designed to populate the demon front line in numbers. Sculpted in ZBrush over a PureRef goblin board, then textured in Substance 3D. The UV-mapping pass was the hardest part — visible texture stretching on the shirt is honest, and a deliberate keep: this is the build that taught me clean unwrapping.',
    icon: Sparkles,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.2, 0.7, 0.2, 1] } },
};

export default function ZBrushSection() {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = characters[selectedIdx];

  return (
    <section id="zbrush" className="relative py-24 lg:py-32" data-testid="zbrush-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="flex items-center gap-4 mb-10"
        >
          <Layers className="text-[#e58a59]" size={20} />
          <span className="text-xs uppercase tracking-[0.3em] text-zinc-400">
            Chapter 01 — Character Sculpts
          </span>
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
            <Brush size={12} className="text-[#e58a59]" /> ZBrush only
          </span>
        </motion.div>

        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="font-display font-light text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-12 max-w-4xl"
          data-testid="zbrush-heading"
        >
          Studies in <span className="italic text-[#e58a59]">sorrow &amp; sinew</span> —
          sculpted from the inside out, 100% ZBrush.
        </motion.h2>

        {/* Character roster + detail panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-testid="character-selector-container">
          {/* Vertical roster (sidebar on desktop, stacked on mobile) */}
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="lg:sticky lg:top-24">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3 pl-1">
                Roster
              </div>
              <div className="flex flex-col gap-3" data-testid="character-selector">
                {characters.map((c, i) => {
                  const isActive = selectedIdx === i;
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.no}
                      type="button"
                      onClick={() => setSelectedIdx(i)}
                      className={`group/sel relative flex items-center gap-4 p-3 rounded-xl border text-left transition-all overflow-hidden ${
                        isActive
                          ? 'border-[#e58a59] bg-[#1a1410] shadow-[0_0_30px_rgba(229,138,89,0.15)]'
                          : 'border-white/10 bg-[#101012] hover:border-white/30'
                      }`}
                      data-testid={`character-card-${i}`}
                      aria-pressed={isActive}
                    >
                      <div className="relative w-16 h-16 lg:w-[72px] lg:h-[72px] rounded-lg overflow-hidden bg-[#0e0e10] border border-white/5 shrink-0">
                        <img
                          src={c.portrait || c.images[0].url}
                          alt={c.title}
                          className={`w-full h-full object-cover transition-transform duration-700 ${isActive ? 'scale-105' : 'group-hover/sel:scale-105'}`}
                        />
                        <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-black/70 text-[9px] uppercase tracking-[0.2em] font-semibold"
                             style={{ color: isActive ? '#e58a59' : '#d4d4d8' }}>
                          {c.no}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon size={11} className={isActive ? 'text-[#e58a59]' : 'text-zinc-500'} />
                          <span className={`text-[9px] uppercase tracking-[0.2em] ${isActive ? 'text-[#e58a59]' : 'text-zinc-500'}`}>
                            {isActive ? '● Viewing' : 'Select'}
                          </span>
                        </div>
                        <div className="font-display text-base lg:text-lg text-white font-bold leading-tight truncate">
                          {c.title}
                        </div>
                        <div className="text-[10px] text-zinc-400 mt-0.5 leading-snug line-clamp-1">
                          {c.role}
                        </div>
                      </div>
                      <span className={`absolute right-0 top-0 bottom-0 w-1 transition-colors ${isActive ? 'bg-[#e58a59]' : 'bg-transparent'}`} />
                    </button>
                  );
                })}
              </div>
              <div className="hidden lg:block mt-6 pl-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                  {characters.length} sculpts · click to preview
                </div>
              </div>
            </div>
          </aside>

          {/* Selected character detail */}
          <div className="lg:col-span-8 xl:col-span-9 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.no}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
              >
                <CharacterBlock character={selected} reverse={false} index={selectedIdx} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function CharacterBlock({ character, reverse, index }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null); // null | { url, label, caption? }
  const Icon = character.icon;
  const current = character.images[active];
  const zoomOpen = !!lightbox;

  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => e.key === 'Escape' && setLightbox(null);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [zoomOpen]);

  const openMain = () => setLightbox({ url: current.url, label: current.label });

  const navigateLightbox = (dir) => {
    if (!lightbox || lightbox.group !== 'process' || !character.process) return;
    const len = character.process.length;
    const nextIdx = (lightbox.groupIndex + dir + len) % len;
    const p = character.process[nextIdx];
    setLightbox({ url: p.url, label: `${p.stage} · ${p.label}`, caption: p.note, group: 'process', groupIndex: nextIdx });
  };

  useEffect(() => {
    if (!zoomOpen || lightbox?.group !== 'process') return;
    const onArrow = (e) => {
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      else if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    document.addEventListener('keydown', onArrow);
    return () => document.removeEventListener('keydown', onArrow);
  }, [zoomOpen, lightbox]);

  return (
    <motion.article
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-100px' }}
      className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
      data-testid={`zbrush-character-${index + 1}`}
    >
      <div
        className={`lg:col-span-7 ${reverse ? 'lg:order-2' : ''} flex flex-col gap-4`}
      >
        <div className="image-mask relative border border-white/5 rounded-lg overflow-hidden glow-card bg-[#1a1a1c]">
          <button
            type="button"
            onClick={openMain}
            className="block w-full cursor-zoom-in"
            aria-label={`Open ${character.title} ${current.label} view full-screen`}
            data-testid={`zbrush-${index + 1}-zoom-trigger`}
          >
            <img
              src={current.url}
              alt={`${character.title} — ${current.label}`}
              className={`w-full h-[420px] sm:h-[520px] lg:h-[640px] object-contain ${
                character.placeholder ? 'object-cover opacity-60' : ''
              }`}
              data-testid={`zbrush-${index + 1}-main-image`}
            />
          </button>
          <div className="absolute top-5 left-5 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 pointer-events-none">
            <Icon size={12} className="text-[#e58a59]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-200">
              {character.no}
            </span>
          </div>
          <div className="absolute top-5 right-5 flex items-center gap-2 pointer-events-none">
            <div className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.2em] text-zinc-200">
              {current.label} view
            </div>
          </div>
          <button
            type="button"
            onClick={openMain}
            className="absolute bottom-5 right-5 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black/65 backdrop-blur-md border border-white/15 text-[10px] uppercase tracking-[0.2em] text-zinc-200 hover:text-[#e58a59] hover:border-[#e58a59]/40 transition-colors"
            data-testid={`zbrush-${index + 1}-zoom-btn`}
          >
            <Maximize2 size={12} /> View full-size
          </button>
        </div>

        {character.images.length > 1 && (
          <div className="grid grid-cols-3 gap-3" data-testid={`zbrush-${index + 1}-thumbnails`}>
            {character.images.map((img, i) => (
              <button
                key={img.label}
                onClick={() => setActive(i)}
                className={`relative h-24 sm:h-28 rounded-md overflow-hidden border transition-all bg-[#1a1a1c] ${
                  active === i
                    ? 'border-[#e58a59] ring-2 ring-[#e58a59]/30'
                    : 'border-white/10 hover:border-white/30'
                }`}
                data-testid={`zbrush-${index + 1}-thumb-${img.label.toLowerCase()}`}
                aria-label={`Show ${img.label} view`}
              >
                <img src={img.url} alt={img.label} className="w-full h-full object-contain" />
                <span className="absolute bottom-1 right-1.5 text-[9px] uppercase tracking-[0.15em] text-white/90 bg-black/55 px-1.5 py-0.5 rounded">
                  {img.label}
                </span>
              </button>
            ))}
          </div>
        )}

        {character.video && (
          <div
            className="relative rounded-lg overflow-hidden border border-white/10 bg-[#0e0e10] glow-card"
            data-testid={`zbrush-${index + 1}-video`}
          >
            <video
              src={character.video}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[260px] sm:h-[320px] lg:h-[360px] object-cover"
            />
            <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/65 backdrop-blur-md border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59] animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-200">
                Niagara VFX · Taunt Preview
              </span>
            </div>
          </div>
        )}

        {character.process && (
          <div
            className="rounded-lg border border-white/10 bg-[#0e0e10] p-5"
            data-testid={`zbrush-${index + 1}-process`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#e58a59]">
                Process Timeline
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Low-Poly → High-Poly
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {character.process.map((p, pi) => (
                <button
                  key={p.stage}
                  type="button"
                  onClick={() => setLightbox({ url: p.url, label: `${p.stage} · ${p.label}`, caption: p.note, group: 'process', groupIndex: pi })}
                  className="group/p relative rounded-md overflow-hidden border border-white/10 bg-[#1a1a1c] hover:border-[#e58a59]/50 transition-colors cursor-zoom-in flex flex-col"
                  data-testid={`zbrush-${index + 1}-process-${p.stage}`}
                  aria-label={`Open ${p.label} stage full-screen`}
                >
                  <div className="aspect-square overflow-hidden bg-[#0e0e10]">
                    <img
                      src={p.url}
                      alt={p.label}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover/p:scale-[1.04]"
                    />
                  </div>
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-[9px] uppercase tracking-[0.2em] text-[#e58a59] font-semibold">
                    {p.stage}
                  </div>
                  <div className="p-3 text-left">
                    <div className="text-xs font-semibold text-white leading-tight">{p.label}</div>
                    <div className="text-[10px] text-zinc-500 mt-1 leading-snug line-clamp-2">{p.note}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={`lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}>
        <div className="text-xs uppercase tracking-[0.25em] text-[#e58a59] mb-4">
          {character.role}
        </div>
        <h3 className="font-display font-bold text-white text-4xl lg:text-5xl leading-tight mb-6">
          {character.title}
        </h3>

        {character.lore && (
          <blockquote className="relative pl-5 border-l-2 border-[#e58a59] mb-6 italic text-zinc-300 text-sm lg:text-base leading-relaxed">
            “{character.lore}”
          </blockquote>
        )}

        <p className="text-zinc-400 text-base leading-relaxed mb-6">{character.blurb}</p>

        <div className="flex flex-wrap gap-2 mb-8">
          {character.tools.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#e58a59]/30 bg-[#e58a59]/10 text-[#e58a59] text-[10px] uppercase tracking-[0.2em]"
            >
              <Brush size={10} /> {t}
            </span>
          ))}
        </div>

        {character.stats && (
          <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
            {character.stats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-xl lg:text-2xl text-white font-bold">
                  {s.value}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {zoomOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={() => setLightbox(null)}
            data-testid={`zbrush-${index + 1}-lightbox`}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="absolute top-5 right-5 w-10 h-10 rounded-full border border-white/15 bg-black/60 flex items-center justify-center text-white hover:text-[#e58a59] hover:border-[#e58a59]/50 transition-colors"
              aria-label="Close full-size view"
              data-testid={`zbrush-${index + 1}-lightbox-close`}
            >
              <X size={18} />
            </button>

            <motion.img
              key={lightbox.url}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
              src={lightbox.url}
              alt={`${character.title} — ${lightbox.label} full size`}
              className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {lightbox.group === 'process' && character.process && character.process.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                  className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/15 bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:text-[#e58a59] hover:border-[#e58a59]/50 transition-colors"
                  aria-label="Previous stage"
                  data-testid={`zbrush-${index + 1}-lightbox-prev`}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                  className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/15 bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:text-[#e58a59] hover:border-[#e58a59]/50 transition-colors"
                  aria-label="Next stage"
                  data-testid={`zbrush-${index + 1}-lightbox-next`}
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 max-w-[90vw] px-4 py-2 rounded-full bg-black/65 border border-white/10 text-[10px] uppercase tracking-[0.25em] text-zinc-200 text-center">
              {character.title} · {lightbox.label}
              {lightbox.caption && <span className="text-zinc-400 normal-case tracking-normal"> — {lightbox.caption}</span>}
              <span className="hidden sm:inline"> · click outside or ESC to close</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}
